import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { ChangeDetectionStrategy, Component, inject, Input, input, OnInit, output, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatButton } from "@angular/material/button";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { OperatorType } from "../../../../classes/flow/Questionnaire/OperatorType";
import { Option } from "../../../../classes/flow/Questionnaire/Option";
import { Question } from "../../../../classes/flow/Questionnaire/Question";
import { QuestionType } from "../../../../classes/flow/Questionnaire/QuestionType";
import { Questionnaire } from "../../../../classes/flow/Questionnaire/Questionnaire";
import { ReportCategory } from "../../../../classes/flow/Questionnaire/ReportCategory";
import { Validation } from "../../../../classes/flow/Questionnaire/Validation";
import { ApplicationService } from "../../../../services/application.service";
import { DialogService } from "../../../../services/dialog.service";
import { QuestionService } from "../../../../services/question.service";
import { SnackbarService } from "../../../../services/snackbar.service";
import { QuestionEditComponent } from "../question-edit/question-edit.component";
import { QuestionnaireEditableState } from "../questionnaire/questionnaire.component";

@Component({
  selector: "app-questionnaire-fresh",
  templateUrl: "./questionnaire-fresh.component.html",
  styleUrls: ["./questionnaire-fresh.component.less"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CdkDropList, CdkDrag, QuestionEditComponent, MatButton, TranslatePipe],
})
export class QuestionnaireFreshComponent implements OnInit {
  private readonly application = inject(ApplicationService);
  private readonly router = inject(Router);
  readonly dialog = inject(DialogService);
  private readonly snack = inject(SnackbarService);
  private readonly questionService = inject(QuestionService);

  @ViewChild("deleteDialog")
  public deleteDialog?: TemplateRef<unknown>;

  @Input("editableState")
  public editableState: QuestionnaireEditableState = {
    general: false,
    questions: false,
    categories: false,
  };

  public questionnaire = input.required<Questionnaire>();
  public operatorTypes = input.required<OperatorType[]>();
  public validations = input.required<Validation[]>();
  public questionTypes = input.required<QuestionType[]>();
  public reportCategories = input.required<ReportCategory[]>();
  public questions = input.required<Question[]>();
  public questionData: QuestionData[];

  public questionDeleted = output<Question>();

  public defaultValidationValue: Validation | null;
  public defaultOperatorTypeValue: OperatorType | null;
  public defaultQuestionTypeValue: QuestionType | null;

  constructor() {
    this.questionData = [];

    this.defaultValidationValue = null;
    this.defaultOperatorTypeValue = null;
    this.defaultQuestionTypeValue = null;
  }

  async ngOnInit(): Promise<void> {
    try {
      this.defaultValidationValue = this.validations().filter((v) => v.name === "none")[0] ?? null;
      this.defaultOperatorTypeValue = this.operatorTypes().filter((o) => o.name === "Equals")[0] ?? null;
      this.defaultQuestionTypeValue = this.questionTypes().filter((q) => q.prefix === "txt")[0] ?? null;

      // this.initQuestions();
    } catch (error) {
      this.snack.error();
      this.router.navigate(["/content/coordinator/all-questionnaires"]);
    }
  }

  // Initialize questionData object
  // private initQuestions() {
  //   const questionnaire = this.questionnaire();
  //   if (!questionnaire || !questionnaire.questions) return;

  //   // Filter questions
  //   const mainQuestions = questionnaire.questions.filter((question) => !question.logic?.parentQuestionId);
  //   const followUpQuestions = questionnaire.questions.filter((question) => !!question.logic?.parentQuestionId);
  //   console.log({ mainQuestions, followUpQuestions });

  //   // Nest followup questions inside their parents
  //   for (const question of followUpQuestions) {
  //     const q = questionnaire.questions.find((parentQuestion) => parentQuestion.id === question.logic?.parentQuestionId);
  //     if (q) q.followUpQuestions.push(question);
  //   }

  //   // Set top layer of questions
  //   this.sortQuestionsRecursive(mainQuestions);

  //   this.questions = mainQuestions;
  // }

  // private sortQuestionsRecursive(questions: Question[]) {
  //   questions.sort((first, second) => {
  //     const firstIndex = (first.index?.split(".") ?? ["0"]).map((v) => Number(v));
  //     const secondIndex = (second.index?.split(".") ?? ["0"]).map((v) => Number(v));

  //     return firstIndex[firstIndex.length - 1] - secondIndex[secondIndex.length - 1];
  //   });

  //   for (const question of questions) {
  //     if (question.followUpQuestions.length) {
  //       this.sortQuestionsRecursive(question.followUpQuestions);
  //     }
  //   }
  // }

  // Reorder question
  public reorderQuestion(event: CdkDragDrop<Question[]>) {
    moveItemInArray(event.item.data, event.previousIndex, event.currentIndex);
  }

  public openDeleteDialog(question: Question) {
    this.dialog.open({
      template: this.deleteDialog!,
      data: question,
    });
  }

  public async deleteQuestion(question: Question) {
    this.application.setLoading(true);

    // if (keepChildren) {
    //    if parentQuestionID === null > remove logic from all children
    //    else update logic to parentQuestionID from question
    // }

    await this.questionService.deleteQuestion(question);
    this.questionDeleted.emit(question);

    this.application.setLoading(false);
  }

  public addMainQuestion() {
    if (!this.editableState.questions) return;
    this.questions().push({
      active: true,
      description: "",
      extraProperties: {},
      followUpQuestions: [],
      name: "",
      placeholder: "",
      required: true,
      questionType: this.defaultQuestionTypeValue!,
      validation: this.defaultValidationValue!,
    });
  }
}

interface QuestionData {
  question: Question;
  control: FormGroup<{
    name: FormControl<string | null>;
    active: FormControl<boolean | null>;
    description: FormControl<string | null>;
    questionType: FormControl<QuestionType | null>;
    placeholder: FormControl<string | null>;
    required: FormControl<boolean | null>;
    index: FormControl<string | null>;
    validation: FormControl<Validation | null>;
    operatorType: FormControl<OperatorType | null>;
    compareAnswer: FormControl<string | null>;
    compareOptionId: FormControl<number | null>;
    compareOptionReference: FormControl<string | null>;
    parentQuestionId: FormControl<number | null>;
    unitPrice: FormControl<number | null>;
  }>;
  subscription?: Subscription;
  options: QuestionOptionData[];
}

interface QuestionOptionData {
  option: Option;
  control: FormControl<string | null>;
}
